import { faInfoCircle } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React from "react";

import { OverlayLoadable } from "@/mk2/components/OverlayLoadable";
import { Placement, Tooltip } from "@/mk2/components/Tooltip";

import styles from "./InfoTooltip.mscss";

interface Props {
    // Text shown in the tooltip bubble.
    text: string;
    placement?: Placement;
    // Optional side-effect fired when the "i" icon is clicked (e.g. copy to clipboard).
    onClick?: () => void;
}

/**
 * Small "i" icon that reveals `text` in a tooltip. Toggles on click/tap (works on
 * both desktop and mobile) and closes on outside tap/click. Intentionally not
 * hover-driven: on touch, a tap fires mouseenter *then* click, which would open
 * and immediately re-close a hover+toggle tooltip.
 */
export const InfoTooltip: React.FunctionComponent<Props> = ({
    text,
    placement = "top",
    onClick,
}) => {
    const [show, setShow] = React.useState(false);
    const triggerRef = React.useRef<HTMLButtonElement>(null);

    React.useEffect(() => {
        if (!show) {
            return undefined;
        }
        const onDocClick = (event: MouseEvent) => {
            if (triggerRef.current && !triggerRef.current.contains(event.target as Node)) {
                setShow(false);
            }
        };
        document.addEventListener("click", onDocClick);
        return () => document.removeEventListener("click", onDocClick);
    }, [show]);

    return (
        <>
            <button
                type="button"
                ref={triggerRef}
                className={styles.InfoTooltip__trigger}
                aria-label={text}
                onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    setShow((value) => !value);
                    onClick?.();
                }}
            >
                <FontAwesomeIcon icon={faInfoCircle} />
            </button>
            <OverlayLoadable
                placement={placement}
                show={show}
                container={() => document.body}
                target={() => triggerRef.current}
            >
                {({ props, arrowProps, placement: resolvedPlacement }) => (
                    <Tooltip
                        placement={resolvedPlacement}
                        containerRef={props.ref}
                        style={props.style}
                        arrowRef={arrowProps.ref}
                        arrowStyle={arrowProps.style}
                    >
                        {text}
                    </Tooltip>
                )}
            </OverlayLoadable>
        </>
    );
};
